import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getMenuIcon, containsPath } from '@/lib/menuUtils';
import { useMenu } from '@/context/MenuContext';

/** 배지는 컴포넌트로 분리하지 않는다 — 렌더마다 새 컴포넌트를 만들면 매번 재마운트된다. */
const renderBadge = (count) => {
  if (!count) return null;
  return (
    <span className="ml-auto shrink-0 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
      {count > 99 ? '99+' : count}
    </span>
  );
};

/**
 * 재귀 메뉴 노드 — depth 제한 없음.
 * 기존 Sidebar 의 2-depth 하드코딩(및 메뉴명 문자열 분기)을 대체한다.
 */
const MenuNode = ({ node, depth = 0, isMobile = false, onNavigate }) => {
  const location = useLocation();
  const { badges, isFavorite, toggleFavorite } = useMenu();

  const children = (node.children || []).filter((child) => child.isDisplay);
  const hasChildren = children.length > 0;
  const isOnPath = containsPath(node, location.pathname);

  const [isOpen, setIsOpen] = useState(isOnPath);

  // 라우터 이동으로 활성 경로가 바뀌면 해당 그룹을 펼친다.
  // (기존 코드는 window.location 을 직접 읽어 이동 시 갱신되지 않았다)
  // 효과 대신 렌더 중 상태 조정 패턴을 써서 불필요한 재렌더를 줄인다.
  const [wasOnPath, setWasOnPath] = useState(isOnPath);
  if (wasOnPath !== isOnPath) {
    setWasOnPath(isOnPath);
    if (isOnPath) setIsOpen(true);
  }

  if (!node.isDisplay) return null;
  if (isMobile && !node.isMobileVisible) return null;

  const badgeCount = node.badgeKey ? badges[node.badgeKey] : 0;
  const indentClass = depth > 0 ? 'pl-3' : '';

  // ── 그룹 노드 ────────────────────────────────────────────────
  if (hasChildren) {
    return (
      <div className={indentClass}>
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
            isOnPath
              ? 'menu-group-open font-semibold'
              : 'text-chrome-text hover:bg-chrome-hover hover:text-chrome-strong'
          )}
        >
          {depth === 0 && (
            <span className={cn('shrink-0', isOnPath ? 'text-chrome-accent' : 'text-chrome-muted')}>
              {getMenuIcon(node.iconClass, 18)}
            </span>
          )}
          <span className="flex-1 truncate font-medium">{node.menuName}</span>
          {renderBadge(badgeCount)}
          <ChevronDown
            size={15}
            className={cn('shrink-0 text-chrome-muted transition-transform', isOpen && 'rotate-180')}
          />
        </button>

        {isOpen && (
          <div className="mt-0.5 space-y-0.5 border-l border-chrome-border pl-2">
            {children.map((child) => (
              <MenuNode
                key={child.menuId}
                node={child}
                depth={depth + 1}
                isMobile={isMobile}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── 링크 없는 빈 노드는 렌더링하지 않음 ─────────────────────
  if (!node.urlPath) return null;

  // ── 리프 노드 ────────────────────────────────────────────────
  const favorited = isFavorite(node.menuId);

  return (
    <div className={cn('group/leaf relative', indentClass)}>
      <NavLink
        to={node.urlPath}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-2 rounded-md py-2 pl-3 pr-8 text-sm transition-colors',
            isActive
              ? 'menu-item-active'
              : 'text-chrome-text hover:bg-chrome-hover hover:text-chrome-strong'
          )
        }
      >
        {depth === 0 && <span className="shrink-0 text-chrome-muted">{getMenuIcon(node.iconClass, 18)}</span>}
        <span className="flex-1 truncate">{node.menuName}</span>
        {renderBadge(badgeCount)}
      </NavLink>

      {/* 즐겨찾기 토글 — 마우스 오버 또는 이미 등록된 경우에만 노출 */}
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          toggleFavorite(node);
        }}
        title={favorited ? '즐겨찾기 해제' : '즐겨찾기 추가'}
        aria-label={favorited ? '즐겨찾기 해제' : '즐겨찾기 추가'}
        className={cn(
          'absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 transition-opacity',
          favorited
            ? 'text-amber-400 opacity-100'
            : 'text-chrome-muted opacity-0 hover:text-amber-400 group-hover/leaf:opacity-100'
        )}
      >
        <Star size={13} fill={favorited ? 'currentColor' : 'none'} />
      </button>
    </div>
  );
};

export default MenuNode;
